import type { CSSProperties } from 'react';

const SPACE_FALLBACKS: Record<string, string> = {
  '0': '0px',
  px: '1px',
  '0-5': '2px',
  '1': '4px',
  '1-5': '6px',
  '2': '8px',
  '2-5': '10px',
  '3': '12px',
  '3-5': '14px',
  '4': '16px',
  '5': '20px',
  '6': '24px',
  '7': '28px',
  '8': '32px',
  '9': '36px',
  '10': '40px',
  '12': '48px',
  '14': '56px',
  '16': '64px',
  '20': '80px',
  '24': '96px',
  '28': '112px',
  '32': '128px',
};

export interface PaddingBox {
  top?: string;
  right?: string;
  bottom?: string;
  left?: string;
}

function toVar(key: string | undefined): string | undefined {
  if (key == null) return undefined;
  const fallback = SPACE_FALLBACKS[key];
  if (!fallback) return undefined;
  if (key === '0') return '0px';
  return `var(--space-${key}, ${fallback})`;
}

/** Convert a PaddingBox of space-token keys to a CSSProperties object.
 *  Only defined keys are included, so callers can spread this on top of
 *  an existing padding shorthand and only override the specified sides. */
export function resolvePaddingStyle(box: PaddingBox | undefined): CSSProperties {
  if (!box) return {};
  const out: CSSProperties = {};
  const t = toVar(box.top);
  const r = toVar(box.right);
  const b = toVar(box.bottom);
  const l = toVar(box.left);
  if (t != null) out.paddingTop = t;
  if (r != null) out.paddingRight = r;
  if (b != null) out.paddingBottom = b;
  if (l != null) out.paddingLeft = l;
  return out;
}

/** Convert a PaddingBox of space-token keys to `{top,right,bottom,left}`
 *  *inset offsets* rather than `padding`. Use this to reserve visible space
 *  around an absolutely-positioned `inset: 0` element (e.g. a full-bleed
 *  background video/image): the containing block for an absolutely
 *  positioned element is its ancestor's *padding box*, which already
 *  includes the padding area — so a plain CSS `padding` on the ancestor has
 *  no visual effect on an `inset: 0` child, it just fills flush through it.
 *  Explicit inset offsets are the only way to actually carve out that gap.
 *  Spread this on top of a base `inset: 0` so undefined sides stay 0. */
export function resolveInsetStyle(box: PaddingBox | undefined): CSSProperties {
  if (!box) return {};
  const out: CSSProperties = {};
  const t = toVar(box.top);
  const r = toVar(box.right);
  const b = toVar(box.bottom);
  const l = toVar(box.left);
  if (t != null) out.top = t;
  if (r != null) out.right = r;
  if (b != null) out.bottom = b;
  if (l != null) out.left = l;
  return out;
}
